import type { Core } from 'cytoscape';
import { EdgeType } from '../../model/edge-model';
import type {
  SimulationEdge,
  SimulationGraph,
  SimulationNode,
  SimulationProcessNode,
} from '../types';

const INPUT_EDGE_TYPES = [
  EdgeType.Agent,
  EdgeType.Instrument,
  EdgeType.Consumption,
];

const OUTPUT_EDGE_TYPES = [EdgeType.Generation, EdgeType.Effect];

const isVisibleElement = (element: any) =>
  element && element.data('display') !== 'none' && !element.data('isMarkerLabel');

export const getInitialNodeValue = (node: SimulationNode): unknown => {
  if (node.exampleValues) {
    const firstValue = node.exampleValues
      .split(',')
      .map(item => item.trim())
      .find(Boolean);
    if (firstValue) {
      if (/^(true|false)$/i.test(firstValue)) {
        return firstValue.toLowerCase() === 'true';
      }
      const numeric = Number(firstValue);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
      return firstValue;
    }
  }

  if (node.type === 'state' || node.type === 'value') {
    return node.label;
  }

  return node.label || node.codeName || '';
};

const topologicalSortProcesses = (processes: Record<string, SimulationProcessNode>) => {
  const indegree = new Map<string, number>();
  const dependents = new Map<string, Set<string>>();

  Object.values(processes).forEach(process => {
    indegree.set(process.id, process.dependencyProcessIds.length);
    process.dependencyProcessIds.forEach(dependencyId => {
      if (!dependents.has(dependencyId)) {
        dependents.set(dependencyId, new Set());
      }
      dependents.get(dependencyId)!.add(process.id);
    });
  });

  const queue = Object.values(processes)
    .filter(process => (indegree.get(process.id) || 0) === 0)
    .map(process => process.id);
  const ordered: string[] = [];

  while (queue.length > 0) {
    const processId = queue.shift()!;
    ordered.push(processId);

    dependents.get(processId)?.forEach(dependentId => {
      const nextValue = (indegree.get(dependentId) || 0) - 1;
      indegree.set(dependentId, nextValue);
      if (nextValue === 0) {
        queue.push(dependentId);
      }
    });
  }

  Object.keys(processes).forEach(processId => {
    if (!ordered.includes(processId)) {
      ordered.push(processId);
    }
  });

  return ordered;
};

export const compileSimulationGraph = (core: Core): SimulationGraph => {
  const cyNodes = core.nodes().filter(isVisibleElement);
  const visibleNodeIds = new Set(cyNodes.map(node => node.id()));
  const nodes: Record<string, SimulationNode> = {};

  cyNodes.forEach(node => {
    const mmRef = node.data('MMRef');
    const type = node.data('type');
    if (!['object', 'process', 'state', 'value'].includes(type)) return;

    nodes[node.id()] = {
      id: node.id(),
      type,
      label: node.data('displayLabel') || node.data('label') || node.id(),
      codeName: mmRef?.codeName || node.data('codeName') || undefined,
      exampleValues: mmRef?.exampleValues || node.data('exampleValues') || '',
      subDiagram: mmRef?.diagram || null,
      functionDefinition: mmRef?.functionDefinition,
      metadata: {
        childGroupType: mmRef?.childGroupType || node.data('childGroupType'),
      },
    };
  });

  const edges: Record<string, SimulationEdge> = {};
  core
    .edges()
    .filter(isVisibleElement)
    .forEach(edge => {
      const sourceId = edge.source().id();
      const targetId = edge.target().id();
      if (!visibleNodeIds.has(sourceId) || !visibleNodeIds.has(targetId)) return;

      edges[edge.id()] = {
        id: edge.id(),
        type: edge.data('type'),
        sourceId,
        targetId,
      };
    });

  const processes: Record<string, SimulationProcessNode> = {};
  const outputProducerByNodeId = new Map<string, string[]>();

  Object.values(edges).forEach(edge => {
    if (!OUTPUT_EDGE_TYPES.includes(edge.type as EdgeType)) return;
    if (nodes[edge.sourceId]?.type !== 'process') return;

    if (!outputProducerByNodeId.has(edge.targetId)) {
      outputProducerByNodeId.set(edge.targetId, []);
    }
    outputProducerByNodeId.get(edge.targetId)!.push(edge.sourceId);
  });

  Object.values(nodes).forEach(node => {
    if (node.type !== 'process') return;

    const inputEdges = Object.values(edges).filter(
      edge =>
        edge.targetId === node.id &&
        INPUT_EDGE_TYPES.includes(edge.type as EdgeType)
    );
    const outputEdges = Object.values(edges).filter(
      edge =>
        edge.sourceId === node.id &&
        OUTPUT_EDGE_TYPES.includes(edge.type as EdgeType)
    );
    const dependencyProcessIds = Array.from(
      new Set(
        inputEdges.flatMap(edge => outputProducerByNodeId.get(edge.sourceId) || [])
      )
    ).filter(processId => processId !== node.id);

    processes[node.id] = {
      ...node,
      type: 'process',
      inputEdges,
      outputEdges,
      dependencyProcessIds,
    };
  });

  const orderedProcessIds = topologicalSortProcesses(processes);
  const inputNodeIds = Array.from(
    new Set(
      Object.values(processes).flatMap(process =>
        process.inputEdges
          .map(edge => edge.sourceId)
          .filter(nodeId => nodes[nodeId] && nodes[nodeId].type !== 'process')
      )
    )
  );
  const rootInputNodeIds = Array.from(
    new Set(
      orderedProcessIds.flatMap(processId => {
        const process = processes[processId];
        if (!process || process.dependencyProcessIds.length > 0) return [];
        return process.inputEdges
          .map(edge => edge.sourceId)
          .filter(nodeId => nodes[nodeId] && nodes[nodeId].type !== 'process');
      })
    )
  );

  return {
    nodes,
    edges,
    processes,
    orderedProcessIds,
    inputNodeIds,
    rootInputNodeIds,
  };
};
